import {
  Badge,
  Box,
  Button,
  ComboboxItem,
  Flex,
  Grid,
  Loader,
  LoadingOverlay,
  Select,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useDebouncedState, useDisclosure, useHotkeys } from "@mantine/hooks";
import {
  IconBarcode,
  IconBuildingSkyscraper,
  IconBuildingWarehouse,
  IconFileTypeCsv,
  IconSearch,
} from "@tabler/icons-react";
import {
  MantineReactTable,
  MRT_ColumnDef,
  MRT_RowSelectionState,
  useMantineReactTable,
} from "mantine-react-table";
import React, { useDeferredValue, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  repositoryDelivery,
  repositoryMdm,
  repositoryPos,
  styleTableMantine,
} from "../../../_base/_const/_constVar";
import _breadcrumb from "../../../_base/component/_layout/_breadcrumb";
import { _localization } from "../../../config/location";
import { MessageResponse } from "../../../model/MessageResponse";
import { getMasterOrgSelect } from "../../../service/getSelectApi";
import {
  changeSearchValue,
  getValueById,
} from "../../../_base/helper/FunctionHelper";
import { BASE_API_MDM } from "../../../config";
import { NotificationExtension } from "../../../_base/extension/NotificationExtension";

type ColumnPinningType = {
  left?: string[];
  right?: string[];
};

const ReportOnComparingSerialInventoryAndQuantityInventory = () => {
  const headerRef = React.useRef<HTMLDivElement>(null);
  const [data, setData] = useState<any[]>([]);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [height, setHeight] = useState(0);
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 20,
  });
  const rowSelectionDeferred =
    useDeferredValue<MRT_RowSelectionState>(rowSelection);
  const [selectIds, setSelectIds] = useState<string[]>([]);
  const [visible, { toggle, open, close }] = useDisclosure(false);

  const location = useLocation();
  const _searchJson = sessionStorage.getItem(String(location.pathname))
    ? JSON.parse(sessionStorage.getItem(String(location.pathname)) ?? "")
    : null;
  const checkedPinSearch = localStorage.getItem("checkedPinSearch") == "true";
  const [search, setSearch] = useState<any>({
    MaChiNhanh: _searchJson && checkedPinSearch ? _searchJson.MaChiNhanh : "",
    MaKho: _searchJson && checkedPinSearch ? _searchJson.MaKho : "",
    MaNhom: _searchJson && checkedPinSearch ? _searchJson.MaNhom : "",
    NoSerial: _searchJson && checkedPinSearch ? _searchJson.NoSerial : "",
  });
  const [keySearch, setKeySearch] = useDebouncedState<string>(
    _searchJson && checkedPinSearch ? _searchJson?.keySearch : "",
    300,
  );

  const [dataBranchSelect, setDataBranchSelect] = useState<any[]>([]);
  const [dataWarehouse, setDataWareHouse] = useState<any[]>([]);

  const columns = React.useMemo<MRT_ColumnDef<any>[]>(
    () => [
      {
        accessorKey: "maVatTu",
        header: "Mã vật tư",
        Cell: ({ renderedCellValue }) => (
          <Badge variant="light" bd={"1px solid blue"}>
            {renderedCellValue}
          </Badge>
        ),
      },
      {
        accessorKey: "tenVatTu",
        header: "Tên vật tư",
      },
      {
        accessorKey: "donViTinh",
        header: "Đơn vị tính",
      },
      {
        accessorKey: "tonCuoiSerial",
        header: "Tồn cuối serial",
      },
      {
        accessorKey: "tonCuoiSoLuong",
        header: "Tồn cuối số lượng",
      },
      {
        accessorKey: "noSerial",
        header: "Có/Không serial",
        Cell: ({ renderedCellValue }) =>
          renderedCellValue ? (
            <Text fw={500} size="12.5px">
              {String(renderedCellValue) === "1" ? "Có Serial" : "Không Serial"}
            </Text>
          ) : (
            <></>
          ),
      },
      {
        accessorKey: "chenhLech",
        header: "Chênh lệch",
      },
    ],
    [],
  );

  const fetchData = async () => {
    setIsLoading(true);
    setIsRefetching(true);
    setIsError(false);
    try {
      let url = `/api/v1/BaoCaoSoSanhTonKho/get-list?Skip=${
        pagination.pageIndex * pagination.pageSize
      }&Take=${pagination.pageSize}`;

      const params = new URLSearchParams();
      Object.entries(search).forEach(([key, value]) => {
        if (Array.isArray(value) && value.length > 0) {
          value.forEach((item) => params.append(key, String(item)));
        } else if (typeof value === "string" && value.length > 0) {
          params.append(key, value);
        }
      });

      if (keySearch.length > 0) {
        url += `&KeySearch=${keySearch?.trim()}`;
      }

      url += `&${params.toString()}`;

      const res = await repositoryMdm.get<MessageResponse<any>>(url);

      if (res && res.success) {
        const result = res.data;
        setData(result);
        setRowCount(res.totalCount);
      } else {
        setData([]);
        setRowCount(0);
      }
    } catch (error) {
      setData([]);
      setRowCount(0);
      setIsError(true);
      console.error(error);
      return;
    } finally {
      setIsLoading(false);
      setIsRefetching(false);
      sessionStorage.setItem(
        String(location.pathname),
        JSON.stringify({
          ...search,
          keySearch: keySearch,
        }),
      );
    }
  };

  const exportDataExcel = async () => {
    open();
    try {
      let url = `?Skip=${pagination?.pageIndex}&Take=${pagination.pageSize}`;

      const params = new URLSearchParams();
      Object.entries(search).forEach(([key, value]) => {
        if (Array.isArray(value) && value.length > 0) {
          value.forEach((item) => params.append(key, String(item)));
        } else if (typeof value === "string" && value.length > 0) {
          params.append(key, value);
        }
      });

      if (keySearch.length > 0) {
        url += `&KeySearch=${keySearch?.trim()}`;
      }

      url += `&${params.toString()}`;

      const baseUrl = `/api/v1/BaoCaoSoSanhTonKho/export-bao-cao-so-sanh-ton-kho${url}`;

      const _url = BASE_API_MDM + baseUrl;
      const token = localStorage.getItem("token");

      const response = await fetch(_url, {
        method: "GET",
        headers: {
          "Content-Type": "application/vnd.ms-excel",
          Authorization: `Bearer ${token?.replace(`"`, "").replace(`"`, "")}`,
        },
      });

      if (!response.ok) {
        NotificationExtension.Fails("Export Excel thất bại !");
      } else {
        const blob = await response.blob();
        const urlss = window.URL.createObjectURL(blob);
        const _name = new Date().toISOString().slice(0, 10);
        let filename =
          "bao_cao_so_sanh_ton_kho_serial_va_ton_kho_so_luong.xlsx";
        const link = document.createElement("a");
        link.href = urlss;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        NotificationExtension.Success("Export Excel thành công !");
      }
    } catch (error) {
      console.log(error);
    } finally {
      close();
    }
  };

  const getDataBranchSelect = async () => {
    const res = await getMasterOrgSelect();

    if (res) {
      const data = res
        ?.filter((item) => item.text && item.value)
        ?.map((item) => ({
          label: `${item.att1} - ${item.text}`,
          value: String(item.att1),
          id: String(item.value),
        }));
      setDataBranchSelect(data);
    }
  };

  const getDataInv = async () => {
    let url = "/api/v1/TblDmInventory/get-list-by-branchs?Skip=0&Take=300";

    if (search.MaChiNhanh) {
      url += `&BranchId=${dataBranchSelect.find((i) => i.value === search.MaChiNhanh)?.id}`;
    }

    const response = await repositoryDelivery.get<MessageResponse<any>>(url);
    if (response && response.success) {
      const result = response.data
        ?.filter((item: any) => item.code && item.name)
        ?.map((item: any) => ({
          label: `${item.code} - ${item.name}`,
          value: String(item.code),
        }));
      setDataWareHouse(result);
    } else {
      setDataWareHouse([]);
    }
  };

  useEffect(() => {
    fetchData();
  }, [pagination?.pageIndex, pagination?.pageSize]);

  useEffect(() => {
    getDataBranchSelect();
  }, []);

  useEffect(() => {
    getDataInv();
  }, [search.MaChiNhanh]);

  const getColumnPinningStorage = () => {
    const columnPinningStorage = localStorage.getItem(
      "columnPinningReportOnComparingSerialInventoryAndQuantityInventory",
    );
    return columnPinningStorage
      ? JSON.parse(columnPinningStorage)
      : {
          left: ["mrt-row-select", "mrt-row-numbers"],
          right: ["action"],
        }; // Giá trị mặc định
  };

  // Lưu columnPinning vào state
  const [columnPinning, setColumnPinning] = useState<ColumnPinningType>(
    getColumnPinningStorage(),
  );
  // Hàm lưu vào localStorage
  useEffect(() => {
    localStorage.setItem(
      "columnPinningReportOnComparingSerialInventoryAndQuantityInventory",
      JSON.stringify(columnPinning),
    );
  }, [columnPinning]);

  useEffect(() => {
    //   startTransition(() => {
    const valuesList = Object.keys(rowSelectionDeferred);
    setSelectIds(valuesList);
    if (valuesList.length < 1) setSelectIds([]);
    else {
      const valuesList = Object.keys(rowSelectionDeferred);
      setSelectIds(valuesList);
    }
    //  });
  }, [rowSelectionDeferred]);

  useEffect(() => {
    const headerHeight = headerRef.current?.offsetHeight || 0;
    const handleResize = () => {
      setHeight(window.innerHeight - (185 + headerHeight));
    };

    handleResize(); // Set initial height
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const table = useMantineReactTable({
    columns,
    data,
    enableColumnResizing: false,
    positionToolbarAlertBanner: "bottom",
    enableRowNumbers: true,
    //add custom action buttons to top-left of top toolbar
    enableTopToolbar: false,
    mantineTopToolbarProps: {
      style: {
        borderBottom: "3px solid rgba(128, 128, 128, 0.5)",
        marginBottom: 5,
      },
    },
    localization: _localization,
    enableRowSelection: true,
    getRowId: (row) => row.id?.toString(),
    initialState: {
      showColumnFilters: false,
      columnPinning: {
        // left: ["mrt-row-numbers", "maVt"],
        // right: ["action"],
        ...columnPinning,
      },
      columnVisibility: { id: true, phone: false, fax: false },
      density: "xs",
    },
    mantineTableContainerProps: {
      style: { maxHeight: height, minHeight: height },
    },
    enableStickyHeader: true,
    manualPagination: true,
    onColumnPinningChange: (newState) => {
      setColumnPinning(newState);
    },
    onRowSelectionChange: setRowSelection,
    rowCount: rowCount,
    onPaginationChange: setPagination,
    mantineTableBodyCellProps: ({ row }) => ({
      style: styleTableMantine,
    }),
    state: {
      isLoading,
      pagination: pagination,
      showAlertBanner: isError,
      showProgressBars: isRefetching,
      showSkeletons: isLoading, //loading for the first time with no data
      rowSelection,
      columnPinning,
    },
    mantineToolbarAlertBannerProps: isError
      ? { color: "red", children: "Lỗi tải dữ liệu !" }
      : undefined,
    mantinePaginationProps: {
      showRowsPerPage: true,
      withEdges: true,
      rowsPerPageOptions: ["20", "50", "100"],
    },
    paginationDisplayMode: "pages",
    enableColumnPinning: true,
    mantineTableProps: {
      striped: false,
    },
    columnFilterDisplayMode: "popover",
    mantineTableBodyRowProps: ({ row }) => ({
      style: { cursor: "pointer" },
    }),
  });

  useHotkeys([
    [
      "Enter",
      () => {
        fetchData();
      },
    ],
  ]);

  return (
    <>
      <div
        ref={headerRef}
        style={{
          width: "100%",
        }}
      >
        <LoadingOverlay
          visible={visible}
          zIndex={1000}
          overlayProps={{ radius: "sm", blur: 5 }}
          loaderProps={{
            children: (
              <Flex direction={"column"} align="center" gap="xs">
                <Loader type="dots" size={35}></Loader>
                {/* <Title size={15}> Đang upload tài liệu...</Title> */}
                <Title size={15}> Đang xử lý....</Title>
              </Flex>
            ),
          }}
        />
        <Flex justify={"space-between"} align={"center"} w={"100%"}>
          <_breadcrumb></_breadcrumb>
          <Flex gap={"md"}>
            <Button
              leftSection={<IconFileTypeCsv size={16} />}
              onClick={() => exportDataExcel()}
            >
              Export Excel
            </Button>
          </Flex>
        </Flex>
        <Grid mt={10} w={"100%"}>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <TextInput
              placeholder="Nhập từ khóa tìm kiếm"
              defaultValue={keySearch}
              onChange={(e) => setKeySearch(e.currentTarget.value)}
              leftSection={<IconSearch color="#15aabf" />}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  fetchData();
                }
              }}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <Select
              placeholder="Có/không Serial"
              leftSection={<IconBarcode color="#15aabf" />}
              data={[
                { label: "Có serial", value: "1" },
                { label: "Không serial", value: "0" },
              ]}
              clearable
              value={search.NoSerial || null}
              onChange={(e) =>
                changeSearchValue(e ? e : "", "NoSerial", setSearch)
              }
            />
          </Grid.Col>
          {/* <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <Select
              placeholder="Chi nhánh"
              leftSection={<IconBuildingSkyscraper color="#15aabf" />}
              data={dataBranchSelect.map((i) => ({
                label: i.label,
                value: i.value,
              }))}
              value={search.MaChiNhanh ? String(search.MaChiNhanh) : null}
              searchable
              clearable
              nothingFoundMessage={"Không tìm thấy dữ liệu !"}
              onChange={(e) =>
                changeSearchValue(e ? e : "", "MaChiNhanh", setSearch)
              }
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <Select
              placeholder="Kho"
              leftSection={<IconBuildingWarehouse color="#15aabf" />}
              data={dataWarehouse}
              value={search.MaKho ? String(search.MaKho) : null}
              searchable
              clearable
              nothingFoundMessage={"Không tìm thấy dữ liệu !"}
              onChange={(e) =>
                changeSearchValue(e ? e : "", "MaKho", setSearch)
              }
            />
          </Grid.Col> */}
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <Button
              onClick={() => fetchData()}
              leftSection={<IconSearch size={14} />}
            >
              Tìm kiếm
            </Button>
          </Grid.Col>
        </Grid>
      </div>
      <Box mt={10}>
        <MantineReactTable table={table} />
      </Box>
    </>
  );
};

export default ReportOnComparingSerialInventoryAndQuantityInventory;
